import {useMutation, useQueryClient} from '@github-ui/react-query'
import {createContext, use, useCallback, useEffect, useRef} from 'react'
import useIsMounted from '@github-ui/use-is-mounted'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  applyCopilotReviewEffortResponse,
  captureCopilotReviewEffort,
  collectCopilotReviewEffort,
  type CopilotReviewEffortRequest,
} from '@github-ui/pull-request-page-data-tooling/copilot-review-effort'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

import {useMergeBoxPageDataQueryKey} from '../../page-data/loaders/use-merge-box-page-data'
import type {StateChangeMutationOptions} from './state-change-mutation-options'

export const DeferredCopilotReviewEffortContext = createContext<(() => CopilotReviewEffortRequest | undefined) | null>(
  null,
)

export function useMarkReadyForReviewMutation({onError, onSuccess, onMutate}: StateChangeMutationOptions = {}) {
  const apiURL = usePageDataUrl(PageData.markReadyForReview)
  const mergeBoxQueryKey = useMergeBoxPageDataQueryKey()
  const queryClient = useQueryClient()
  const captureRequest = use(DeferredCopilotReviewEffortContext)
  const protectedRequest = useRef<CopilotReviewEffortRequest | undefined>(undefined)
  const isMounted = useIsMounted()

  useEffect(() => () => protectedRequest.current?.dismissFailure?.(), [])

  const mutation = useMutation({
    onMutate: () => ({
      rollback: onMutate?.(),
    }),
    mutationFn: async () => {
      const picker = captureRequest ? null : document.querySelector('[data-copilot-deferred-review-effort-picker]')
      let request: CopilotReviewEffortRequest | undefined
      let submission
      let reviewEffort
      try {
        request = captureRequest?.()
        submission = captureCopilotReviewEffort(picker)
        reviewEffort = request?.fields ?? collectCopilotReviewEffort(picker)
      } catch (error) {
        reportError(error)
      }
      // Keep retry protection continuous before retiring its earlier failed attempt.
      if (protectedRequest.current !== request) protectedRequest.current?.dismissFailure?.()
      protectedRequest.current = request
      let json
      try {
        const response = await fetchWithErrorHandling(apiURL, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
          body: reviewEffort,
        })
        json = await parseJSONWithBetterErrors(response)
        throwErrorsIfBadResponse(response, json)
      } catch (error) {
        request?.fail()
        if (!isMounted()) request?.dismissFailure?.()
        throw error
      }
      try {
        request?.acceptResponse(json)
        applyCopilotReviewEffortResponse(submission, json)
      } catch (error) {
        // Unusable optional metadata must not make an already-successful Ready request retryable.
        reportError(error)
      }
      return json
    },
    onSuccess: () => {
      onSuccess?.()
      return queryClient.invalidateQueries({queryKey: mergeBoxQueryKey}, {cancelRefetch: false})
    },
    onError: (e: Error, _variables, onMutateResult) => {
      onMutateResult?.rollback?.()
      onError?.(e)
    },
  })
  const {reset: resetMutation} = mutation
  const reset = useCallback(() => {
    protectedRequest.current?.dismissFailure?.()
    resetMutation()
  }, [resetMutation])
  return {...mutation, reset}
}
